import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api';
import packageInfo from '../../package.json';
import { Heatmap } from '../components/ui';
import { computeStreak, buildHeatMap, dateKey, mondayOf, weekdayIndex, WEEKDAYS } from '../lib/activity';

function getCurrentWeekRange() {
  const startOfWeek = new Date();
  startOfWeek.setHours(0, 0, 0, 0);
  startOfWeek.setDate(startOfWeek.getDate() - ((startOfWeek.getDay() + 6) % 7));
  const startOfNextWeek = new Date(startOfWeek);
  startOfNextWeek.setDate(startOfNextWeek.getDate() + 7);
  const dateKey = (date) => [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');

  return { start: dateKey(startOfWeek), end: dateKey(startOfNextWeek) };
}

function computeWeeklyAttendance(sessions, weekRange) {
  return sessions.filter((session) =>
    session.total_sets > 0 && session.date >= weekRange.start && session.date < weekRange.end
  ).length;
}

function computeVolumeTrend(sessions) {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const dow = (now.getDay() + 6) % 7;
  const thisMonday = new Date(now);
  thisMonday.setDate(now.getDate() - dow);
  const lastMonday = new Date(thisMonday);
  lastMonday.setDate(thisMonday.getDate() - 7);
  const lastSunday = new Date(thisMonday);
  lastSunday.setDate(thisMonday.getDate() - 1);
  const thisWeek = sessions
    .filter(s => new Date(s.date + 'T12:00:00') >= thisMonday)
    .reduce((acc, s) => acc + (s.total_volume || 0), 0);
  const lastWeek = sessions
    .filter(s => { const d = new Date(s.date + 'T12:00:00'); return d >= lastMonday && d <= lastSunday; })
    .reduce((acc, s) => acc + (s.total_volume || 0), 0);
  return { pct: lastWeek > 0 ? (thisWeek - lastWeek) / lastWeek * 100 : null };
}

function StatCard({ value, label, icon, trend }) {
  return (
    <div className="card dashboard-stat-card" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      <div style={{ fontSize: 36 }}>{icon}</div>
      <div>
        <div className="stat-value">{value}</div>
        <div className="stat-label">{label}</div>
        {trend?.pct != null && (
          <div style={{ fontSize: 11, color: trend.pct >= 0 ? 'var(--success)' : 'var(--danger)', marginTop: 2 }}>
            {trend.pct >= 0 ? '\u2191' : '\u2193'} {Math.abs(Math.round(trend.pct))}% vs sem. pr\u00e9c.
          </div>
        )}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { data: programs = [] } = useQuery({ queryKey: ['programs'], queryFn: api.getPrograms });
  const { data: achievements } = useQuery({ queryKey: ['achievements'], queryFn: api.getAchievements });
  const GROUP_INFO = Object.fromEntries(programs.map((p) => [p.name, { icon: p.icon, focus: p.name, color: p.color }]));
  const { data: sessions = [], isLoading: loadingSessions } = useQuery({
    queryKey: ['sessions'],
    queryFn: api.getSessions,
  });

  const { data: changelog = [], isError: changelogError } = useQuery({
    queryKey: ['changelog'],
    queryFn: async () => {
      const response = await fetch('/changelog.json');
      if (!response.ok) throw new Error('Impossible de charger le changelog');
      const entries = await response.json();
      if (!Array.isArray(entries)) throw new Error('Format du changelog invalide');
      return entries;
    },
    staleTime: 60000,
  });

  const { data: bests = [] } = useQuery({
    queryKey: ['bests'],
    queryFn: api.getBests,
  });

  const lastUpdated = changelog.reduce(
    (latest, entry) => (entry.date > latest ? entry.date : latest),
    ''
  );
  const weekRange = getCurrentWeekRange();
  const recentSessions = sessions.slice(0, 5);

  const totalVolume = sessions.reduce((acc, s) => acc + (s.total_volume || 0), 0);

  const streak = useMemo(() => computeStreak(sessions), [sessions]);
  const weeklyAttendance = useMemo(
    () => computeWeeklyAttendance(sessions, weekRange),
    [sessions, weekRange.start, weekRange.end]
  );
  const volumeTrend = useMemo(() => computeVolumeTrend(sessions), [sessions]);
  const heatMap = useMemo(() => buildHeatMap(sessions, 16), [sessions]);
  const weeklyRecords = useMemo(
    () => bests
      .filter((best) => best.max_weight > 0 && best.best_date >= weekRange.start && best.best_date < weekRange.end)
      .sort((a, b) => b.max_weight - a.max_weight)
      .slice(0, 5),
    [bests, weekRange.start, weekRange.end]
  );
  const attendanceScore = Math.min(weeklyAttendance, 5);
  const attendanceStatus = weeklyAttendance >= 5
    ? { label: 'Au top', icon: '🔥' }
    : weeklyAttendance === 4
      ? { label: 'Très bien', icon: '💪' }
      : weeklyAttendance === 3
        ? { label: 'Correct', icon: '🙂' }
        : weeklyAttendance === 2
          ? { label: 'À améliorer', icon: '😅' }
          : weeklyAttendance === 1
            ? { label: 'Nul', icon: '😬' }
            : { label: 'À démarrer', icon: '🏁' };

  const groups = Object.entries(GROUP_INFO);

  const { data: schedule = [] } = useQuery({ queryKey: ['schedule'], queryFn: api.getSchedule });
  const todayIdx = weekdayIndex();
  const todayKey = dateKey(new Date());
  const planned = schedule.find((p) => p.weekday === todayIdx);
  const doneToday = sessions.find((s) => s.date === todayKey && s.total_sets > 0);
  const weekDays = useMemo(() => {
    const monday = mondayOf(new Date());
    return WEEKDAYS.map((name, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      const key = dateKey(d);
      return {
        name: name.slice(0, 3),
        isToday: i === todayIdx,
        done: sessions.some((s) => s.date === key && s.total_sets > 0),
        plan: schedule.find((p) => p.weekday === i),
      };
    });
  }, [sessions, schedule, todayIdx]);

  return (
    <div>
      <h1 className="page-title">Tableau de bord</h1>
      <p className="page-subtitle">Bienvenue dans ton suivi de programme de musculation 🏋️</p>

      <div className="card today-card">
        <div className="today-main">
          <div className="today-kicker">{WEEKDAYS[todayIdx]} · aujourd'hui</div>
          {planned ? (
            <>
              <div className="today-title">{planned.icon} {planned.program_name}</div>
              <div className="muted">{doneToday ? 'Séance déjà enregistrée aujourd\'hui, bravo !' : 'Séance prévue au planning'}</div>
              <Link
                to={`/workout?day=${encodeURIComponent(planned.program_name)}&autostart=1`}
                className="btn btn-primary today-cta"
              >
                {doneToday ? '▶ Reprendre la séance' : '▶ Démarrer la séance'}
              </Link>
            </>
          ) : (
            <>
              <div className="today-title">{doneToday ? 'Séance faite ✅' : 'Pas de séance planifiée'}</div>
              <div className="muted">Choisis un programme ou planifie ta semaine.</div>
              <div className="today-actions">
                <Link to="/workout" className="btn btn-primary today-cta">💪 Choisir une séance</Link>
                <Link to="/programs?planning=1" className="btn btn-ghost">📅 Planifier</Link>
              </div>
            </>
          )}
        </div>
        <div className="week-strip" aria-label="Semaine en cours">
          {weekDays.map((d) => (
            <div key={d.name} className={`week-day${d.isToday ? ' today' : ''}${d.done ? ' done' : ''}`} title={d.plan ? d.plan.program_name : 'Repos'}>
              <span className="week-day-name">{d.name}</span>
              <span className="week-day-dot">{d.done ? '✓' : d.plan ? d.plan.icon : '·'}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="dashboard-stats-grid">
        <StatCard value={sessions.length} label="Séances totales" icon="📅" />
        <div className="card dashboard-stat-card" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ fontSize: 36 }}>⚖️</div>
          <div>
            <div className="stat-value">{(totalVolume / 1000).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} t</div>
            <div className="stat-label">Volume total · <span style={{ color: 'var(--text)' }}>{Math.round(totalVolume).toLocaleString('fr-FR')} kg</span></div>
            {volumeTrend.pct != null && (
              <div style={{ fontSize: 11, color: volumeTrend.pct >= 0 ? 'var(--success)' : 'var(--danger)', marginTop: 2 }}>
                {volumeTrend.pct >= 0 ? '↑' : '↓'} {Math.abs(Math.round(volumeTrend.pct))}% vs sem. préc.
              </div>
            )}
          </div>
        </div>
        <StatCard value={bests.length} label="Exercices suivis" icon="📈" />
        <StatCard
          value={sessions[0]?.date ? new Date(sessions[0].date).toLocaleDateString('fr-FR') : '—'}
          label="Dernière séance"
          icon="🕐"
        />
        <StatCard
          value={streak > 0 ? `${streak} sem.` : '—'}
          label={streak > 0 ? 'Semaines consécutives' : 'Pas de streak'}
          icon={streak >= 4 ? '🔥' : '📆'}
        />
        <div className="card dashboard-stat-card" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ fontSize: 36 }}>{attendanceStatus.icon}</div>
          <div style={{ flex: 1 }}>
            <div className="stat-value">{attendanceScore}/5</div>
            <div className="stat-label">Assiduité cette semaine</div>
            <div
              role="meter"
              aria-label="Séances enregistrées cette semaine"
              aria-valuemin={0}
              aria-valuemax={5}
              aria-valuenow={attendanceScore}
              style={{ display: 'flex', gap: 4, marginTop: 8 }}
            >
              {Array.from({ length: 5 }, (_, index) => (
                <div
                  key={index}
                  style={{
                    height: 6,
                    flex: 1,
                    borderRadius: 99,
                    background: index < attendanceScore ? 'var(--accent2)' : 'var(--bg3)',
                  }}
                />
              ))}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 4 }}>{attendanceStatus.label}</div>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 32 }}>
        <div className="card-header">
          <span className="card-title">🏆 Records de poids cette semaine</span>
        </div>
        {weeklyRecords.length === 0 ? (
          <div style={{ color: 'var(--text2)', fontSize: 13 }}>
            Aucun nouveau record de poids cette semaine. À toi de jouer !
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {weeklyRecords.map((record) => (
              <div key={record.exercise_id} className="session-item">
                <div>
                  <div className="session-day-name">{record.exercise_name}</div>
                  <div className="session-date">
                    {new Date(`${record.best_date}T00:00:00`).toLocaleDateString('fr-FR')}
                  </div>
                </div>
                <strong style={{ color: 'var(--accent2)' }}>{record.max_weight} kg</strong>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Heat Map */}
      {sessions.length > 0 && (
        <div className="card" style={{ marginBottom: 32 }}>
          <div className="card-header" style={{ marginBottom: 12 }}>
            <span className="card-title">🗓 Activité</span>
            <span style={{ fontSize: 12, color: 'var(--text2)' }}>16 dernières semaines</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <Heatmap grid={heatMap} />
          </div>
        </div>
      )}

      {achievements && (
        <Link to="/trophies" className="card trophy-widget" style={{ display: 'block', marginBottom: 24, textDecoration: 'none' }}>
          <div className="level-head">
            <div className="level-badge">{achievements.level.index}</div>
            <div style={{ flex: 1 }}>
              <div className="level-name">🏆 {achievements.level.name}</div>
              <div className="muted small">
                {achievements.badges.filter((b) => b.unlocked).length} / {achievements.badges.length} badges · {achievements.level.points} pts
              </div>
            </div>
            <span className="muted">Voir →</span>
          </div>
        </Link>
      )}

      <div className="grid grid-2" style={{ marginBottom: 32 }}>
        {/* Programme de la semaine */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Programme d'entraînement</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {groups.map(([group, info]) => (
              <Link
                key={group}
                to={`/workout?day=${encodeURIComponent(group)}&autostart=1`}
                style={{ textDecoration: 'none' }}
              >
                <div
                  className="dashboard-program-link"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 14px',
                    borderRadius: 8,
                    background: 'var(--bg3)',
                    border: '1px solid var(--border)',
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                  }}
                >
                  <span style={{ fontSize: 22 }}>{info.icon}</span>
                  <div className="dashboard-program-name">
                    <div style={{ fontWeight: 700 }}>{info.focus}</div>
                  </div>
                  <div
                    style={{
                      marginLeft: 'auto',
                      width: 8, height: 8,
                      borderRadius: '50%',
                      background: info.color,
                    }}
                  />
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Dernières séances */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Dernières séances</span>
            <Link to="/history" className="btn btn-ghost btn-sm">Voir tout</Link>
          </div>
          {loadingSessions ? (
            <div className="loading"><div className="spinner" />Chargement...</div>
          ) : recentSessions.length === 0 ? (
            <div className="empty-state">
              <div className="icon">📭</div>
              <p>Aucune séance enregistrée</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {recentSessions.map((s) => (
                <Link key={s.id} to={`/history/${s.id}`} className="session-item">
                  <div>
                    <div className="session-day-name">{GROUP_INFO[s.day]?.focus || s.day}</div>
                    <div className="session-date">{new Date(s.date).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 700, color: 'var(--accent2)' }}>{s.total_sets} séries</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={{ textAlign: 'center', color: 'var(--text2)', fontSize: 12, padding: '8px 0 16px' }}>
        MuscuApp v{packageInfo.version} · Dernière mise à jour :{' '}
        {changelogError
          ? 'indisponible'
          : lastUpdated
            ? new Date(`${lastUpdated}T00:00:00`).toLocaleDateString('fr-FR')
            : '—'}
      </div>
    </div>
  );
}
